import type { Config } from "@/components/chessground/config";
import type { Key } from "@/components/chessground/types";
import type { Square } from "chess.js";
import { Chess } from "chess.js";
import { useMemo } from "react";

// Helper function to convert chess.js square to chessground key
export function squareToKey(square: Square): Key {
  return square as Key;
}

// Helper function to convert chessground key to chess.js square
export function keyToSquare(key: Key): Square {
  return key as Square;
}

// Helper function to get legal moves for chessground
export function getLegalMoves(game: Chess): Map<Key, Key[]> {
  const moves = new Map<Key, Key[]>();
  const legalMoves = game.moves({ verbose: true });

  for (const move of legalMoves) {
    const from = squareToKey(move.from);
    const to = squareToKey(move.to);

    if (!moves.has(from)) {
      moves.set(from, []);
    }
    moves.get(from)!.push(to);
  }

  return moves;
}

// Helper function to get allowed moves using the allowedMoves function from game store
export function getAllowedMovesForChessground(
  game: Chess,
  allowedMoves: ((square: Square) => string[]) | null
): Map<Key, Key[]> {
  if (!allowedMoves) {
    return getLegalMoves(game);
  }

  const moves = new Map<Key, Key[]>();

  // Get all squares on the board that have pieces
  const board = game.board();
  for (let rank = 0; rank < 8; rank++) {
    for (let file = 0; file < 8; file++) {
      const piece = board[rank][file];
      if (piece) {
        const square = (String.fromCharCode(97 + file) + (8 - rank)) as Square;
        const destinations = allowedMoves(square);

        if (destinations.length > 0) {
          const from = squareToKey(square);
          const to = destinations.map((dest) => squareToKey(dest as Square));
          moves.set(from, to);
        }
      }
    }
  }

  return moves;
}

// Helper function to get all allowed moves (legal + power moves) for validation
export function getAllowedMovesMap(
  game: Chess,
  allowedMoves: ((square: Square) => string[]) | null
): Map<Key, Key[]> {
  if (!allowedMoves) {
    return getLegalMoves(game);
  }

  const moves = new Map<Key, Key[]>();

  // Get all squares on the board
  for (let rank = 1; rank <= 8; rank++) {
    for (let file = 0; file < 8; file++) {
      const square = (String.fromCharCode(97 + file) + rank) as Square;
      const piece = game.get(square);

      if (piece) {
        const destinations = allowedMoves(square);
        if (destinations.length > 0) {
          const from = squareToKey(square);
          const to = destinations.map((dest) => squareToKey(dest as Square));
          moves.set(from, to);
        }
      }
    }
  }

  return moves;
}

// Helper function to validate if a move is allowed
export function isValidMove(
  from: Square,
  to: Square,
  game: Chess,
  allowedMoves: ((square: Square) => string[]) | null
): boolean {
  const movesMap = getAllowedMovesMap(game, allowedMoves);
  const fromKey = squareToKey(from);
  const toKey = squareToKey(to);

  const destinations = movesMap.get(fromKey);
  return destinations ? destinations.includes(toKey) : false;
}

// Helper function to get premove destinations for player pieces
export function getPremoveDestinations(
  game: Chess,
  playerColor: "w" | "b",
  allowedMoves: ((square: Square) => string[]) | null
): Map<Key, Key[]> {
  const moves = new Map<Key, Key[]>();

  // Create a copy of the game and force it to be the player's turn
  const gameCopy = new Chess(game.fen());

  // Manually set the turn to the player's color to calculate potential moves
  const fenParts = gameCopy.fen().split(" ");
  fenParts[1] = playerColor; // Set active color
  const modifiedFen = fenParts.join(" ");

  try {
    const tempGame = new Chess(modifiedFen);

    if (allowedMoves) {
      // Use custom allowed moves function
      const board = tempGame.board();
      for (let rank = 0; rank < 8; rank++) {
        for (let file = 0; file < 8; file++) {
          const piece = board[rank][file];
          if (piece && piece.color === playerColor) {
            const square = (String.fromCharCode(97 + file) +
              (8 - rank)) as Square;
            const destinations = allowedMoves(square);

            if (destinations.length > 0) {
              const from = squareToKey(square);
              const to = destinations.map((dest) =>
                squareToKey(dest as Square)
              );
              moves.set(from, to);
            }
          }
        }
      }
    } else {
      // Use legal moves for the player's pieces
      const legalMoves = tempGame.moves({ verbose: true });
      for (const move of legalMoves) {
        const piece = tempGame.get(move.from);
        if (piece && piece.color === playerColor) {
          const from = squareToKey(move.from);
          const to = squareToKey(move.to);

          if (!moves.has(from)) {
            moves.set(from, []);
          }
          moves.get(from)!.push(to);
        }
      }
    }
  } catch (error) {
    console.warn("Error calculating premove destinations:", error);
    // Fallback to empty moves if FEN manipulation fails
  }

  return moves;
}

interface UseChessgroundConfigOptions {
  game: Chess;
  playerColor: "w" | "b";
  isPlayerTurn: boolean;
  showCoordinates?: boolean;
  showLegalMoves?: boolean;
  enableAnimations?: boolean;
  animationSpeed?: number;
  allowedMoves?: ((square: Square) => string[]) | null;
  onMove?: (orig: Key, dest: Key) => void;
  onSelect?: (key: Key) => void;
  premovable?: boolean;
}

export function useChessgroundConfig({
  game,
  playerColor,
  isPlayerTurn,
  showCoordinates = true,
  showLegalMoves = true,
  enableAnimations = true,
  animationSpeed = 2,
  allowedMoves = null,
  onMove,
  onSelect,
  premovable = true,
}: UseChessgroundConfigOptions): Config {
  return useMemo(() => {
    const config: Config = {
      fen: game.fen(),
      orientation: playerColor === "w" ? "white" : "black",
      turnColor: game.turn() === "w" ? "white" : "black",
      check: game.inCheck(),
      coordinates: showCoordinates,
      animation: {
        enabled: enableAnimations,
        duration: enableAnimations ? 200 + Number(animationSpeed) * 100 : 0,
      },
      highlight: {
        lastMove: true,
        check: true,
      },
      premovable: {
        enabled: premovable,
        showDests: true,
        castle: true,
      },
      predroppable: {
        enabled: premovable,
      },
      movable: {
        free: false,
        color: premovable
          ? "both"
          : isPlayerTurn
            ? playerColor === "w"
              ? "white"
              : "black"
            : undefined,
        dests: showLegalMoves
          ? premovable && !isPlayerTurn
            ? getPremoveDestinations(game, playerColor, allowedMoves)
            : getAllowedMovesForChessground(game, allowedMoves)
          : new Map(),
        showDests: showLegalMoves,
      },
      draggable: {
        enabled: true,
        showGhost: true,
      },
      selectable: {
        enabled: true,
      },
      events: {
        move: onMove,
        select: onSelect,
      },
    };

    return config;
  }, [
    game,
    playerColor,
    isPlayerTurn,
    showCoordinates,
    showLegalMoves,
    enableAnimations,
    animationSpeed,
    allowedMoves,
    onMove,
    onSelect,
    premovable,
  ]);
}

